import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, Sparkles, ArrowLeft, MessageCircle } from "lucide-react";
import { BQ } from "../../lib/bharatQuest";

export default function PersonalityChat({ user, onXpEarned }) {
  const [personalities, setPersonalities] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { BQ.personalities().then((d) => setPersonalities(d.personalities)); }, []);

  if (active) return <ChatPanel user={user} personality={active} onBack={() => setActive(null)} onXpEarned={onXpEarned} />;

  return (
    <div data-testid="bq-personalities">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Talk to Legends</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] max-w-3xl">
        Six personalities. <span className="mythos-gold">Real history. Real conversations.</span>
      </h2>
      <p className="text-[#E8EBF0]/65 mt-2 max-w-2xl">Each gives you +5 XP per conversation (first 10/day). Stay curious — they answer in character, with their actual works and ideas.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {personalities.map((p, i) => (
          <motion.button
            key={p.slug}
            onClick={() => setActive(p)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            data-testid={`bq-personality-${p.slug}`}
            className="text-left vault-card rounded-2xl p-6 transition hover:border-[#C9A66B] border border-[#2A3349] group"
          >
            <div className="w-14 h-14 rounded-full mythos-gold-bg grid place-items-center text-[#0E0F12] font-mythos font-extrabold text-xl mb-4">
              {p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="font-mythos font-extrabold text-[#F4EFE6] text-xl">{p.name}</div>
            <div className="text-[11px] tracking-[0.16em] uppercase mythos-gold font-bold mt-1">{p.title}</div>
            <div className="text-xs text-[#E8EBF0]/55 mt-2">{p.tag}</div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold mythos-gold opacity-0 group-hover:opacity-100 transition">
              <MessageCircle size={14} /> Start conversation
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

function ChatPanel({ user, personality, onBack, onXpEarned }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: personality.intro },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [xpToast, setXpToast] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await BQ.personalityChat(user.username, personality.slug, userMsg, sessionId);
      if (!sessionId) setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.reply }]);
      if (r.xp_earned > 0) {
        setXpToast(r.xp_earned);
        setTimeout(() => setXpToast(0), 2500);
        onXpEarned?.();
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I cannot reach you right now. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="bq-chat-panel">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[#E8EBF0]/65 hover:text-[#F4EFE6] mb-4 text-sm" data-testid="bq-chat-back">
        <ArrowLeft size={14} /> All legends
      </button>
      <div className="vault-card rounded-2xl border border-[#2A3349] overflow-hidden">
        {/* Personality header */}
        <div className="border-b mythos-rule px-5 py-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full mythos-gold-bg grid place-items-center text-[#0E0F12] font-mythos font-extrabold text-lg">
            {personality.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="font-mythos font-extrabold text-[#F4EFE6]">{personality.name}</div>
            <div className="text-xs mythos-gold font-bold">{personality.title}</div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="px-5 py-6 h-[55vh] overflow-y-auto thin-scroll space-y-4" data-testid="bq-chat-messages">
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                m.role === "user"
                  ? "mythos-gold-bg text-[#0E0F12] rounded-br-md"
                  : "bg-[#0F1623] border border-[#2A3349] text-[#F4EFE6] rounded-bl-md"
              }`} data-testid={`bq-chat-msg-${m.role}`}>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
              </div>
            </motion.div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="bq-chat-loading">
              <div className="bg-[#0F1623] border border-[#2A3349] rounded-2xl rounded-bl-md px-4 py-3 inline-flex items-center gap-2">
                <Loader2 className="animate-spin mythos-gold" size={14} />
                <span className="text-xs text-[#E8EBF0]/65">{personality.name.split(" ")[0]} is thinking…</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t mythos-rule px-5 py-4 flex gap-2 bg-[#0E0F12]">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Ask ${personality.name.split(" ")[0]} anything…`}
            disabled={loading}
            maxLength={600}
            className="flex-1 bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-full px-5 py-3 text-[#F4EFE6] placeholder-[#7d8294] disabled:opacity-60"
            data-testid="bq-chat-input"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            className="mythos-gold-bg text-[#0E0F12] rounded-full px-5 py-3 font-semibold disabled:opacity-50 transition inline-flex items-center gap-2"
            data-testid="bq-chat-send"
          >
            <Send size={14} />
          </button>
        </div>
      </div>

      {/* XP toast */}
      {xpToast > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          className="fixed bottom-6 right-6 mythos-gold-bg text-[#0E0F12] rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 shadow-2xl z-50"
          data-testid="bq-chat-xp-toast"
        >
          <Sparkles size={14} /> +{xpToast} XP
        </motion.div>
      )}
    </div>
  );
}
